import React, {useState, useEffect, useRef, useLayoutEffect, useMemo} from 'react';
import styled from 'styled-components';
import {useSelector} from 'react-redux';

import * as am4core from '@amcharts/amcharts4/core';
import * as am4maps from '@amcharts/amcharts4/maps';
import am4geodata_worldLow from '@amcharts/amcharts4-geodata/worldLow';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';

const StyledStoresWorldMap = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledChart = styled.div`
  width: 100%;
  height: 300px; /* Reduce height for dashboard */
  overflow: hidden;
  border: 1px solid ${props => props.theme.mapOutline};
  border-radius: 10px;
`;

const LegendContainer = styled.div`
  display: flex;
  gap: 24px;
  padding: 12px 0;
  justify-content: flex-start;
  margin-top: 12px;
`;

const LegendItemLabel = styled.div`
  font-size: 1rem;
  color: ${props => props.theme.text};
  font-family: 'Trade Gothic';
  font-weight: bold;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ColorDot = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${props => props.color};
  flex-shrink: 0;
`;

const LegendLabel = styled.span`
  font-size: 0.75rem;
  color: ${props => props.theme.text};
  font-family: 'Trade Gothic';
`;

function StoresWorldMap({continent, storeMapData, viewMode}) {
  const theme = useSelector(state => state.theme);
  const themeSelected = theme[theme.select];

  const reduxCoordinates = useSelector(state => state.data.app.landingPageCoordinates);
  const [coordinatesData, setCoordinatesData] = useState([]);

  useEffect(() => {
    if (reduxCoordinates.dataRetrieved) {
      setCoordinatesData(reduxCoordinates.data);
    }
  }, [reduxCoordinates]);

  /* Chart code */
  // Themes begin
  am4core.useTheme(am4themes_animated);
  // Themes end

  const chartRef = useRef(null);

  useLayoutEffect(() => {
    // Create map instance
    let chart = am4core.create('chartdiv', am4maps.MapChart);

    // Set map definition
    chart.geodata = am4geodata_worldLow;

    // Set projection
    chart.projection = new am4maps.projections.Miller();

    // Map Settings
    chart.maxZoomLevel = 32;
    chart.homeZoomLevel = 1;
    chart.seriesContainer.draggable = true;
    chart.chartContainer.wheelable = true;

    // Create map polygon series
    let polygonSeries = chart.series.push(new am4maps.MapPolygonSeries());

    // Exclude Antartica
    polygonSeries.exclude = ['AQ'];

    // Make map load polygon (like country names) data from GeoJSON
    polygonSeries.useGeodata = true;

    // Configure series
    let polygonTemplate = polygonSeries.mapPolygons.template;
    polygonTemplate.tooltipText = '';
    polygonTemplate.polygon.fillOpacity = 1;
    polygonTemplate.fill = am4core.color(themeSelected.mapColor);
    polygonTemplate.stroke = am4core.color(themeSelected.mapOutline);

    // Create hover state and set alternative fill color
    // let hs = polygonTemplate.states.create("hover");
    // hs.properties.fill = '#9EDAE5';

    // Add image series
    let imageSeries = chart.series.push(new am4maps.MapImageSeries());
    imageSeries.mapImages.template.propertyFields.longitude = 'longitude';
    imageSeries.mapImages.template.propertyFields.latitude = 'latitude';
    imageSeries.mapImages.template.tooltipText = '{title}';
    imageSeries.mapImages.template.propertyFields.url = 'url';

    let circle = imageSeries.mapImages.template.createChild(am4core.Circle);
    circle.radius = 1;
    circle.propertyFields.fill = 'color';

    imageSeries.data = storeMapData;

    // Define continent centers and zoom levels
    const continentZooms = {
      'North America': {latitude: 40, longitude: -100, zoomLevel: 3},
      EMEA: {latitude: 40, longitude: 35, zoomLevel: 2.5}, // Europe & Middle East
      'Greater China': {latitude: 35, longitude: 105, zoomLevel: 4},
      APLA: {latitude: -10, longitude: 120, zoomLevel: 3.5},
    };

    // After chart is created
    if (continent && continentZooms[continent]) {
      const {latitude, longitude, zoomLevel} = continentZooms[continent];
      chart.events.on('ready', function () {
        chart.zoomToGeoPoint({latitude, longitude}, zoomLevel, true);
      });
    }

    chartRef.current = chart;
    return () => {
      chart.dispose();
    };
  }, [continent, storeMapData, themeSelected]);

  return (
    <StyledStoresWorldMap>
      <StyledChart id="chartdiv"></StyledChart>
      <LegendContainer>
        <LegendItem>
          <LegendItemLabel>
            {viewMode ? 'Itam mismatch % :' : 'Inactive devices % :'}
          </LegendItemLabel>
        </LegendItem>
        <LegendItem>
          <ColorDot color="#0f820fff" />
          <LegendLabel>≥70%</LegendLabel>
        </LegendItem>
        <LegendItem>
          <ColorDot color="#dede1dff" />
          <LegendLabel>31-69%</LegendLabel>
        </LegendItem>
        <LegendItem>
          <ColorDot color="#bb1c1cff" />
          <LegendLabel>≤30%</LegendLabel>
        </LegendItem>
      </LegendContainer>
    </StyledStoresWorldMap>
  );
}

// Custom comparison function for React.memo
// Only rerender if continent or storeMapData changes
const areEqual = (prevProps, nextProps) => {
  // Rerender if continent changes
  if (prevProps.continent !== nextProps.continent) {
    return false;
  }

  // Rerender if storeMapData array length changes
  if (prevProps.storeMapData?.length !== nextProps.storeMapData?.length) {
    return false;
  }

  // Rerender if storeMapData content changes (deep comparison of colors/positions)
  if (prevProps.storeMapData?.length > 0) {
    return prevProps.storeMapData.every((item, index) => {
      const nextItem = nextProps.storeMapData[index];
      return (
        item.latitude === nextItem.latitude &&
        item.longitude === nextItem.longitude &&
        item.color === nextItem.color &&
        item.title === nextItem.title &&
        item.url === nextItem.url
      );
    });
  }

  // No rerender if nothing changed
  return true;
};

export default React.memo(StoresWorldMap, areEqual);
